import React from 'react';
import { motion } from 'framer-motion';

const Hero: React.FC = () => {
  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.substring(1);
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      const headerHeight = 80;
      const targetPosition = targetElement.offsetTop - headerHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section
      id="home"
      className="relative bg-brand-gradient text-brand-white pt-[120px] pb-24 text-center overflow-hidden"
    >
      {/* Subtle background elements */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <motion.div
          className="absolute top-10 left-[5%] w-32 h-32 bg-brand-yellow rounded-full blur-3xl"
          animate={{ y: [0, -30, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 right-[10%] w-40 h-40 bg-brand-pink rounded-full blur-3xl"
          animate={{ y: [0, 30, 0], scale: [1, 1.3, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
      </div>

      {/* Animated Cartoon Characters - Random Positions */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top Left - Elephant */}
        <motion.div
          className="absolute top-[15%] left-[5%] text-4xl md:text-6xl lg:text-7xl"
          initial={{ x: -150, opacity: 0, rotate: -20 }}
          animate={{ x: 0, opacity: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.3, type: 'spring', bounce: 0.5 }}
        >
          <motion.div
            animate={{ 
              y: [0, -15, 0],
              rotate: [0, 5, 0, -5, 0]
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
          >
            🐘
          </motion.div>
        </motion.div>

        {/* Top Right - Lion */}
        <motion.div
          className="absolute top-[12%] right-[8%] text-5xl md:text-7xl lg:text-8xl"
          initial={{ x: 150, opacity: 0, rotate: 20 }}
          animate={{ x: 0, opacity: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.4, type: 'spring', bounce: 0.5 }}
        >
          <motion.div
            animate={{ 
              y: [0, -18, 0],
              rotate: [0, -8, 0, 8, 0]
            }}
            transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
          >
            🦁
          </motion.div>
        </motion.div>

        {/* Left Side - Giraffe */}
        <motion.div
          className="absolute top-[45%] left-[2%] text-5xl md:text-8xl lg:text-9xl"
          initial={{ y: 200, opacity: 0, scale: 0.5 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.6, type: 'spring', bounce: 0.4 }}
        >
          <motion.div
            animate={{ 
              y: [0, -25, 0],
              rotate: [0, -5, 0, 5, 0]
            }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ filter: 'drop-shadow(2px 2px 5px rgba(0,0,0,0.3))' }}
          >
            🦒
          </motion.div>
        </motion.div>

        {/* Right Side - Monkey */}
        <motion.div
          className="absolute top-[40%] right-[1%] text-4xl md:text-6xl lg:text-7xl"
          initial={{ y: -200, opacity: 0, scale: 0.5 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.7, type: 'spring', bounce: 0.4 }}
        >
          <motion.div
            animate={{ 
              y: [0, -20, 0],
              rotate: [0, 10, 0, -10, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
          >
            🐵
          </motion.div>
        </motion.div>

        {/* Bottom Left - Bear */}
        <motion.div
          className="absolute bottom-[15%] left-[8%] text-4xl md:text-6xl lg:text-7xl"
          initial={{ x: -150, y: 100, opacity: 0 }}
          animate={{ x: 0, y: 0, opacity: 1 }}
          transition={{ duration: 1.3, delay: 0.8, type: 'spring', bounce: 0.5 }}
        >
          <motion.div
            animate={{ 
              y: [0, -12, 0],
              rotate: [0, -6, 0, 6, 0]
            }}
            transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
          >
            🐻
          </motion.div>
        </motion.div>

        {/* Bottom Right - Panda */}
        <motion.div
          className="absolute bottom-[18%] right-[10%] text-4xl md:text-6xl lg:text-7xl"
          initial={{ x: 150, y: 100, opacity: 0 }}
          animate={{ x: 0, y: 0, opacity: 1 }}
          transition={{ duration: 1.3, delay: 0.9, type: 'spring', bounce: 0.5 }}
        >
          <motion.div
            animate={{ 
              y: [0, -15, 0],
              rotate: [0, 8, 0, -8, 0],
              scale: [1, 1.05, 1]
            }}
            transition={{ duration: 3.8, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
            style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
          >
            🐼
          </motion.div>
        </motion.div>

        {/* Top Center - Butterfly */}
        <motion.div
          className="absolute top-[10%] left-[35%] md:left-[25%] text-3xl md:text-4xl lg:text-5xl"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.5, delay: 1, type: 'spring', bounce: 0.6 }}
        >
          <motion.div
            animate={{ 
              y: [0, -20, 0, -10, 0],
              x: [0, 10, 0, -10, 0],
              rotate: [0, 10, -10, 5, 0]
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ filter: 'drop-shadow(1px 1px 3px rgba(0,0,0,0.2))' }}
          >
            🦋
          </motion.div>
        </motion.div>

        {/* Top Center Right - Bee */}
        <motion.div
          className="absolute top-[20%] right-[28%] md:right-[20%] text-2xl md:text-3xl lg:text-4xl"
          initial={{ y: -80, x: 80, opacity: 0 }}
          animate={{ y: 0, x: 0, opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.1, type: 'spring', bounce: 0.7 }}
        >
          <motion.div
            animate={{ 
              y: [0, -15, -5, -12, 0],
              x: [0, 8, -8, 5, 0],
              rotate: [0, -15, 15, -10, 0]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
            style={{ filter: 'drop-shadow(1px 1px 3px rgba(0,0,0,0.2))' }}
          >
            🐝
          </motion.div>
        </motion.div>

        {/* Left Center - Koala */}
        <motion.div
          className="absolute top-[30%] left-[5%] md:left-[7%] text-3xl md:text-5xl lg:text-6xl"
          initial={{ x: -120, opacity: 0, scale: 0.6 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 1.2, type: 'spring', bounce: 0.5 }}
        >
          <motion.div
            animate={{ 
              y: [0, -10, 0],
              rotate: [0, 5, 0, -5, 0]
            }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
            style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
          >
            🐨
          </motion.div>
        </motion.div>

        {/* Right Center - Fox */}
        <motion.div
          className="absolute top-[55%] right-[4%] md:right-[6%] text-3xl md:text-5xl lg:text-6xl"
          initial={{ x: 120, opacity: 0, scale: 0.6 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 1.3, type: 'spring', bounce: 0.5 }}
        >
          <motion.div
            animate={{ 
              y: [0, -13, 0],
              rotate: [0, -7, 0, 7, 0]
            }}
            transition={{ duration: 4.3, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
            style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
          >
            🦊
          </motion.div>
        </motion.div>

        {/* Bottom Center Left - Penguin */}
        <motion.div
          className="absolute bottom-[25%] left-[22%] md:left-[18%] text-3xl md:text-4xl lg:text-5xl"
          initial={{ y: 150, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.4, type: 'spring', bounce: 0.6 }}
        >
          <motion.div
            animate={{ 
              y: [0, -8, 0],
              rotate: [0, -10, 0, 10, 0],
              x: [0, 5, 0, -5, 0]
            }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
            style={{ filter: 'drop-shadow(1px 1px 3px rgba(0,0,0,0.3))' }}
          >
            🐧
          </motion.div>
        </motion.div>

        {/* Bottom Center Right - Rabbit */}
        <motion.div
          className="absolute bottom-[22%] right-[30%] md:right-[25%] text-3xl md:text-4xl lg:text-5xl"
          initial={{ y: 150, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1.4, delay: 1.5, type: 'spring', bounce: 0.7 }}
        >
          <motion.div
            animate={{ 
              y: [0, -18, -5, -15, 0],
              rotate: [0, 8, 0, -8, 0]
            }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.7 }}
            style={{ filter: 'drop-shadow(1px 1px 3px rgba(0,0,0,0.3))' }}
          >
            🐰
          </motion.div>
        </motion.div>
      </div>

      {/* Floating subtle elements - minimal and elegant */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-32 left-[15%] text-3xl opacity-40"
          animate={{ 
            y: [0, -15, 0],
            rotate: [0, 10, 0]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          ⭐
        </motion.div>
        
        <motion.div
          className="absolute top-40 right-[15%] text-3xl opacity-40"
          animate={{ 
            y: [0, -12, 0],
            rotate: [0, -10, 0]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        >
          ✨
        </motion.div>

        <motion.div
          className="absolute bottom-40 left-[20%] text-3xl opacity-30"
          animate={{ 
            y: [0, -10, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        >
          🎈
        </motion.div>

        <motion.div
          className="absolute bottom-32 right-[20%] text-3xl opacity-30"
          animate={{ 
            y: [0, -12, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        >
          🎈
        </motion.div>
      </div>

      {/* Main Content */}
      <motion.div 
        className="relative z-10 max-w-5xl mx-auto flex flex-col items-center px-4"
      >
        {/* Main heading - clean and elegant */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="mb-8"
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-lg">
            <span className="text-brand-white">Neev</span>
            <span className="text-brand-pink"> Play</span>
            <span className="text-brand-white ml-6">School</span>
          </h1>
        </motion.div>

        {/* Tagline - cleaner design */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          className="mb-12"
        >
          <motion.div 
            className="text-brand-dark border-3 border-brand-yellow rounded-bubble px-10 py-4 font-bold text-lg md:text-xl bg-brand-yellow tracking-wider uppercase shadow-lg"
            whileHover={{ y: -3, scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            ROOTED IN VALUES, GROWING WITH LOVE
          </motion.div>
        </motion.div>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
          className="text-lg md:text-xl mb-16 leading-relaxed max-w-3xl text-brand-white"
          style={{ textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)' }}
        >
          Where children learn through hands-on exploration in a completely
          <span className="text-brand-yellow font-semibold"> screen-free environment</span>
          <br />
          <span className="text-brand-blue font-medium">Discover, Play, Grow! 🌱</span>
        </motion.p>

        {/* CTA buttons - cleaner 3D style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: 'easeOut' }}
          className="flex flex-wrap gap-6 justify-center"
        >
          <motion.a
            href="#contact"
            onClick={(e) => handleSmoothScroll(e, '#contact')}
            className="bg-brand-yellow border-2 border-brand-yellow rounded-bubble px-10 py-4 font-bold text-lg"
            style={{
              boxShadow: '0 4px 0 rgba(234, 179, 8, 0.7), 0 8px 20px rgba(0, 0, 0, 0.3)',
              color: '#1a1a1a',
            }}
            whileHover={{ 
              y: -5,
              boxShadow: '0 6px 0 rgba(236, 72, 153, 0.7), 0 10px 25px rgba(0, 0, 0, 0.4)',
              backgroundColor: '#ec4899',
              borderColor: '#ec4899',
              color: '#ffffff',
            }}
            whileTap={{ 
              y: 1,
              boxShadow: '0 2px 0 rgba(234, 179, 8, 0.7), 0 4px 10px rgba(0, 0, 0, 0.3)',
            }}
          >
            Enroll Now 🎉
          </motion.a>
          
          <motion.a
            href="#about"
            onClick={(e) => handleSmoothScroll(e, '#about')}
            className="border-2 border-brand-blue bg-white rounded-bubble px-10 py-4 font-bold text-lg"
            style={{
              boxShadow: '0 4px 0 rgba(59, 130, 246, 0.7), 0 8px 20px rgba(0, 0, 0, 0.3)',
              color: '#3b82f6',
            }}
            whileHover={{ 
              y: -5,
              boxShadow: '0 6px 0 rgba(59, 130, 246, 0.8), 0 10px 25px rgba(0, 0, 0, 0.4)',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
            }}
            whileTap={{ 
              y: 1,
              boxShadow: '0 2px 0 rgba(59, 130, 246, 0.7), 0 4px 10px rgba(0, 0, 0, 0.3)',
            }}
          >
            Learn More 📚
          </motion.a>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero; 